import { type RequestServiceOptions, utils } from '@atlaskit/util-service-support';

/**
 * RAOC: account-id resolution for the combined-config SSR cache fill.
 *
 * `useRovoConfig` no longer receives a `userId` prop. The only remaining need
 * for the Atlassian Account ID (AAID) is to write the SSR localStorage cache
 * entry under the AAID-based inner hash that the Confluence SSR preloader
 * (`@confluence/search-route-preloader` → `preloadSearch.ts`) reads back.
 *
 * This module resolves the current user's AAID via a local lookup of
 * `/gateway/api/me`, mirroring the existing pattern in
 * `@confluence/error-app` (`fetchGet('/gateway/api/me') → account_id`).
 *
 * It lives in `rovo-connectors-config` (the orchestrator of the SSR-fill)
 * rather than `search-client`, so the network call is owned by the package
 * that decides when to fill the cache; `search-client` only owns the cache
 * key shape.
 *
 * The result is memoised for the lifetime of the page: the AAID does not
 * change within a session, and the SSR-fill may run on multiple config
 * resolutions, so we fetch at most once.
 */

const RELATIVE_URL_ME = '/gateway/api/me';

type MeResponse = {
	account_id?: string | null;
};

/**
 * In-memory, page-lifetime memo of the in-flight (or resolved) `/me` request.
 * Reset only by a full page load. Stores the promise (not just the value) so
 * concurrent callers share a single network request.
 */
let currentUserAccountIdPromise: Promise<string | null> | undefined;

const requestCurrentUserAccountId = async (): Promise<string | null> => {
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		},
	};

	try {
		const response = await utils.requestService<MeResponse>({ url: RELATIVE_URL_ME }, options);
		return response?.account_id ?? null;
	} catch {
		// Best-effort: a failed /me lookup only degrades the SSR warm path
		// (the next SSR falls back to a network preload). Never throw.
		return null;
	}
};

/**
 * Resolve the current user's Atlassian Account ID via `/gateway/api/me`.
 *
 * - Memoised: fetches at most once per page load (shared in-flight promise).
 * - SSR-safe: returns `null` when there is no `window` (the SSR-fill is a
 *   client-only concern and must never run server-side).
 * - Never throws: resolves to `null` on any failure.
 */
export const fetchCurrentUserAccountId = async (): Promise<string | null> => {
	// SSR guard — the fill only ever runs in the browser.
	if (typeof window === 'undefined') {
		return null;
	}

	if (!currentUserAccountIdPromise) {
		currentUserAccountIdPromise = requestCurrentUserAccountId().then((accountId) => {
			// Do not memoise a null result: allow a later retry to succeed
			// (e.g. transient /me failure during early page load).
			if (accountId == null) {
				currentUserAccountIdPromise = undefined;
			}
			return accountId;
		});
	}

	return currentUserAccountIdPromise;
};

/**
 * Test-only: clear the page-lifetime memo so each test starts cold.
 */
export const __resetCurrentUserAccountIdCacheForTests = (): void => {
	currentUserAccountIdPromise = undefined;
};
